import { DatePipe } from '@angular/common';
import { Component, Input, OnInit, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';
import { HandleLoadingState } from '../../../../decorators/handle-loading-state.decorator';
import { getClaimKey } from '../../../../functions/get-claim-key';
import {
  CustomField,
  LookupOption,
} from '../../../../models/api/view/claim/details/custom-fields/custom-field';
import { CustomFieldLookup } from '../../../../models/api/view/claim/details/custom-fields/custom-field-lookup';
import { SaveCustomField } from '../../../../models/api/view/claim/details/custom-fields/save-custom-field';
import { Action } from '../../../../models/core/action';
import { ExportService } from '../../../../services/export/export.service';
import { CustomFieldExportMapper } from '../../../../utilities/custom-field-export-mapper';
import { customFieldDecimal } from '../../../../utilities/custom-field-number-validators';
import { integer } from '../../../../validators/integer';
import { BaseComponent } from '../../../base/base.component';
import { ButtonComponent } from '../../../core/buttons/button.component';
import {
  ExportButtonComponent,
  ExportFormat,
} from '../../../core/buttons/export-button.component';
import { DatePickerComponent } from '../../../core/controls/date-picker.component';
import { InputComponent } from '../../../core/controls/input.component';
import { RadioButtonComponent } from '../../../core/controls/radio-button.component';
import { SelectComponent } from '../../../core/controls/select.component';
import { TagComponent } from '../../../core/labels/tag.component';
import { ToastComponent } from '../../../core/labels/toast.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import { CardComponent } from '../../../core/panels/card.component';
import { EmptyStateComponent } from '../../../core/panels/empty-state.component';
import { LoadingPanelComponent } from '../../../core/panels/loading-panel.component';
import { TogglePanelComponent } from '../../../core/panels/toggle-panel.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';

@Component({
  selector: 'app-custom-fields',
  imports: [
    TableComponent,
    ColumnComponent,
    RouterModule,
    TagComponent,
    ActionRowComponent,
    ButtonComponent,
    TogglePanelComponent,
    CardComponent,
    InputComponent,
    ExportButtonComponent,
    TooltipModule,
    DatePickerComponent,
    SelectComponent,
    ToastComponent,
    RadioButtonComponent,
    EmptyStateComponent,
    LoadingPanelComponent,
  ],
  template: `
    <app-loading-panel [state]="loading ? 'Loading' : 'Loaded'" [error]="error">
      <ng-template #loaded>
        @if (!isLeave && canEdit && hasEditableFields) {
          <app-action-row>
            @if (saved) {
              <app-toast
                center1
                type="Success"
                [dismiss]="true"
                [dismissSeconds]="10"
                (dismissed)="saved = false"
              >
                Changes have been saved</app-toast
              >
            }
            <app-button
              right1
              label="Save changes"
              [disabled]="fg?.pristine || fg?.invalid"
              (click)="save()"
            ></app-button>
            <app-button
              right2
              label="Reset"
              emphasis="Low"
              [disabled]="fg?.pristine"
              (click)="buildForm()"
            ></app-button>
          </app-action-row>
        }
        <div class="tw-pb-3"></div>

        @if (!customFields.length) {
          <div class="tw-pt-6">
            <app-empty-state icon="fa-table fa-light" label="No fields" />
          </div>
        } @else {
          <app-toggle-panel>
            <ng-template #left>
              <app-export-button (export)="onExport($event)" />
            </ng-template>
            @for (tabNum of this.tabs; track tabNum; let i = $index) {
              <app-card
                [label]="tabNames[tabNum]"
                [expanded]="i === 0"
                labelCase="none"
                [showExpand]="true"
                type="Medium"
                theme="Light"
              >
                <app-table
                  [dataSource]="groupedCustomFields[tabNum]"
                  [showPaginator]="false"
                  [scrollable]="false"
                  dataKey="fieldNumber"
                  recordLabel="Field"
                  [sortField]="'fieldNumber'"
                  [sortOrder]="1"
                  [stripedRows]="false"
                  theme="Dark"
                >
                  <app-column name="fieldNumber" label="Field #">
                    <ng-template let-data>
                      <div class="tw-flex tw-items-center">
                        <div class="tw-pr-2">{{ data.fieldNumber }}</div>
                        @if (data.hrFeed && showHrFeedTags) {
                          <app-tag
                            type="Purple"
                            pTooltip="Provided by client HR feed"
                            tooltipPosition="top"
                            >HR</app-tag
                          >
                        }
                      </div>
                    </ng-template>
                  </app-column>
                  <app-column name="longLabel" label="Field">
                    <ng-template let-data>
                      <span class="tw-font-bold">{{ data.longLabel }}</span>
                    </ng-template>
                  </app-column>
                  <app-column name="value" label="Value">
                    <ng-template let-data>
                      @if (data.isEditable && canEdit) {
                        @if (data.isLookupField) {
                          <app-select
                            optionLabel="value"
                            optionValue="value"
                            filterBy="value,description"
                            [options]="getSelectOptions(data)"
                            [control]="
                              $any(fg.controls['field-' + data.fieldNumber])
                            "
                            [showHelpText]="false"
                            [filter]="true"
                            (show)="onDropdownShow(data)"
                            [loading]="data.isLoadingOptions"
                            [virtualScroll]="false"
                            [dropdownOptionTemplate]="lookupDropdownOption"
                          />
                        } @else if (data.dataType === 'date') {
                          <app-date-picker
                            label=""
                            [control]="
                              $any(fg.controls['field-' + data.fieldNumber])
                            "
                            [showClear]="true"
                            [showHelpText]="false"
                          />
                        } @else if (data.dataType === 'logical') {
                          <div class="tw-flex tw-items-center tw-gap-2">
                            <app-radio-button
                              optionLabel="label"
                              optionValue="key"
                              [options]="yesNo"
                              [control]="
                                $any(fg.controls['field-' + data.fieldNumber])
                              "
                              [showHelpText]="false"
                              [vertical]="false"
                              [showClear]="true"
                            />
                          </div>
                        } @else if (data.dataType === 'character') {
                          <app-input
                            label=""
                            [control]="
                              $any(fg.controls['field-' + data.fieldNumber])
                            "
                            [maxlength]="data.format"
                            [showClear]="true"
                            [type]="'Text'"
                          />
                        } @else if (data.dataType === 'decimal') {
                          <app-input
                            label=""
                            [control]="
                              $any(fg.controls['field-' + data.fieldNumber])
                            "
                            [showClear]="true"
                            [type]="'Text'"
                            (keypress)="onDecimalKeypress($event)"
                          />
                        } @else if (data.dataType === 'integer') {
                          <app-input
                            label=""
                            [control]="
                              $any(fg.controls['field-' + data.fieldNumber])
                            "
                            [showClear]="true"
                            [type]="'Text'"
                            (keypress)="onIntegerKeypress($event)"
                          />
                        }
                      } @else {
                        <span class="">{{ data.value }}</span>
                      }
                    </ng-template>
                  </app-column>
                  @if (!isLeave) {
                    <app-column
                      name="validationDesc"
                      label="Validation code description"
                    />
                  }
                </app-table>
                <div class="tw-pt-8"></div>
              </app-card>
              <div class="tw-pt-8"></div>
            }
          </app-toggle-panel>
        }
      </ng-template>
      <ng-template #lookupDropdownOption let-item="item">
        <span class="tw-font-bold">{{ item.value }}</span>
        @if (item.description) {
          <span class="tw-text-[var(--sed-black-60-color)]">
            — {{ item.description }}</span
          >
        }
      </ng-template>
    </app-loading-panel>
  `,
  styles: ``,
})
export class CustomFieldsComponent extends BaseComponent implements OnInit {
  private datePipe = inject(DatePipe);

  override ngOnDestroy(): void {
    this.clearLookupFieldSubscriptions();
    super.ngOnDestroy();
  }
  exportService = inject(ExportService);

  private refreshCache = false;

  @Input()
  canEdit = false;

  fg: FormGroup;

  saved = false;
  tabs: number[] = [];
  tabNames: Record<number, string> = {};
  customFields: CustomField[] = [];
  groupedCustomFields: Record<number, CustomField[]> = {};

  isLeave: boolean;

  loading = false;
  error: any = null;

  hasEditableFields = false;

  /** OPTIONS-EMPLOYEE-DETAIL (session); DS insured also needs RestDsLa + CustDisp (matches HR fields tab). */
  showHrFeedTags = false;

  private lookupFieldSubscriptions: Subscription[] = [];

  select: Action[] = [
    { label: 'One', key: '1' },
    { label: 'Two', key: '2' },
    { label: 'Three', key: '3' },
  ];

  yesNo: Action[] = [
    { label: 'Yes', key: 'Y' },
    { label: 'No', key: 'N' },
  ];

  @HandleLoadingState()
  async ngOnInit() {
    this.showHrFeedTags = this.authorizeHrFeedTags();
    this.isLeave = this.view.currentClaim().recordSource === 'T';
    await this.loadCustomFields();
  }

  private async loadCustomFields(): Promise<void> {
    const fields = await this.fetchCustomFields(this.refreshCache);
    this.refreshCache = false;
    this.applyCustomFields(fields);
  }

  private async fetchCustomFields(
    refreshCache: boolean,
  ): Promise<CustomField[]> {
    if (this.isLeave) {
      const absenceUid = this.view.currentClaim().fileNumber;
      return this.api.customFields.leaveCustomFields(
        getClaimKey(),
        absenceUid,
        Number(this.view.currentClaim().contractNumber),
        refreshCache,
      );
    }
    if (this.view.currentClaim().recordSource === 'R') {
      return [];
    }
    return this.api.customFields.customFields(
      getClaimKey(),
      Number(this.view.currentClaim().contractNumber),
      refreshCache,
    );
  }

  private applyCustomFields(customFields: CustomField[]): void {
    this.customFields = customFields;
    this.tabs = this.getTabNumKeys();
    for (const tabNum of this.tabs) {
      this.tabNames[tabNum] = this.getTabName(tabNum);
    }
    this.hasEditableFields = this.customFields.some((f) => f.isEditable);
    this.groupCustomFieldsByTabNum();
    this.buildForm();
  }

  private authorizeHrFeedTags(): boolean {
    let ok = this.session.authorize('EmployeeDetail');
    const claim = this.view.currentClaim();
    if (claim?.lineOfBusiness === 'DS') {
      ok =
        ok &&
        this.view.authorize('RestDsLa') &&
        this.view.authorize('CustDisp');
    }
    return ok;
  }

  groupCustomFieldsByTabNum(): void {
    this.groupedCustomFields = {};
    for (const field of this.customFields) {
      const tabNum = field.tabNum ?? 0;
      if (!this.groupedCustomFields[tabNum]) {
        this.groupedCustomFields[tabNum] = [];
      }
      this.groupedCustomFields[tabNum].push(field);
    }
  }

  getTabNumKeys(): number[] {
    const uniqueTabs = this.customFields
      .filter(
        (field, index, array) =>
          array.findIndex((f) => f.tabNum === field.tabNum) === index,
      )
      .sort((a, b) => (a.tabOrder ?? 0) - (b.tabOrder ?? 0))
      .map((field) => field.tabNum ?? 0);

    return uniqueTabs;
  }

  getTabName(tabNum: number): string {
    const tab = this.customFields.find((field) => field.tabNum === tabNum);
    return tab?.tabName || `Tab ${tabNum}`;
  }

  buildForm() {
    const group: any = {};
    for (const field of this.customFields) {
      if (field.isEditable) {
        const validators = this.getValidatorsForField(field);

        const initialValue = field.value;

        group['field-' + field.fieldNumber] = new FormControl<string>(
          initialValue,
          validators,
        );
      }
    }
    this.fg = new FormGroup(group);
    this.setupLookupFieldSubscriptions();
  }

  private clearLookupFieldSubscriptions(): void {
    for (const sub of this.lookupFieldSubscriptions) {
      sub.unsubscribe();
    }
    this.lookupFieldSubscriptions = [];
  }

  private setupLookupFieldSubscriptions(): void {
    this.clearLookupFieldSubscriptions();
    if (!this.fg) {
      return;
    }

    for (const field of this.customFields) {
      if (!field.isEditable || !field.isLookupField) {
        continue;
      }

      const control = this.fg.controls['field-' + field.fieldNumber];
      if (!control) {
        continue;
      }

      this.syncValidationDescription(field, control.value);
      const sub = control.valueChanges.subscribe((value) => {
        this.syncValidationDescription(field, value);
      });
      this.lookupFieldSubscriptions.push(sub);
    }
  }

  private syncValidationDescription(
    field: CustomField,
    value: string | null | undefined,
  ): void {
    if (!field.isLookupField) {
      return;
    }

    if (!value?.trim()) {
      field.validationDesc = '';
      return;
    }

    const description = this.lookupDescriptionForValue(field, value);
    if (description !== undefined) {
      field.validationDesc = description;
    }
  }

  private lookupDescriptionForValue(
    field: CustomField,
    value: string,
  ): string | undefined {
    const match = (field.selectOptions ?? []).find((opt) => opt.value === value);
    if (match) {
      return match.description ?? '';
    }
    if (!field.lookupOptionsLoaded) {
      return undefined;
    }
    return '';
  }

  private sortLookupOptions(options: LookupOption[]): LookupOption[] {
    return [...options].sort((a, b) =>
      (a.value ?? '').localeCompare(b.value ?? '', undefined, {
        numeric: true,
        sensitivity: 'base',
      }),
    );
  }

  async save() {
    if (this.fg.invalid) {
      return;
    }
    this.saved = false;
    const fieldsToSave = this.getChangedFields();
    await this.api.customFields.saveCustomFields(getClaimKey(), fieldsToSave);
    this.refreshCache = true;
    await this.loadCustomFields();
    this.fg.markAsPristine();
    this.saved = true;
  }

  async loadLookupOptions(field: CustomField): Promise<void> {
    field.isLoadingOptions = true;
    if (field.lookupOptionsLoaded || !field.isLookupField) {
      field.isLoadingOptions = false;
      return;
    }

    const lookupParams: CustomFieldLookup = {
      contNum: Number(this.view.currentClaim().contractNumber),
      fieldNumber: field.fieldNumber,
      valueXref: field.valueXref,
      currentValue: field.value,
    };

    const lookupOptions = await this.api.customFields.customFieldLookup(
      getClaimKey(),
      lookupParams,
    );

    field.selectOptions = this.sortLookupOptions(
      Array.isArray(lookupOptions)
        ? lookupOptions.filter((opt) => opt.num && opt.num.trim() !== '')
        : [],
    );
    field.lookupOptionsLoaded = true;
    field.isLoadingOptions = false;

    const control = this.fg?.controls['field-' + field.fieldNumber];
    if (control) {
      this.syncValidationDescription(field, control.value);
    }
  }

  getSelectOptions(field: CustomField): any[] {
    if (field.isLoadingOptions && !field.lookupOptionsLoaded) {
      return [
        {
          num: '',
          value: '',
          description: '',
        },
      ];
    }

    return this.sortLookupOptions(field.selectOptions ?? []);
  }

  async onDropdownShow(field: CustomField): Promise<void> {
    if (!field.lookupOptionsLoaded && field.isLookupField) {
      await this.loadLookupOptions(field);
    }
  }

  onIntegerKeypress(event: KeyboardEvent): void {
    if (this.isControlKey(event)) {
      return;
    }
    if (event.key.length === 1 && !/[\d-]/.test(event.key)) {
      event.preventDefault();
    }
  }

  onDecimalKeypress(event: KeyboardEvent): void {
    if (this.isControlKey(event)) {
      return;
    }
    if (event.key.length === 1 && !/[\d,.-]/.test(event.key)) {
      event.preventDefault();
    }
  }

  private isControlKey(event: KeyboardEvent): boolean {
    return (
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      event.key === 'Backspace' ||
      event.key === 'Delete' ||
      event.key === 'Tab' ||
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight' ||
      event.key === 'Home' ||
      event.key === 'End'
    );
  }

  private getValidatorsForField(field: CustomField): ValidatorFn[] {
    const validators: ValidatorFn[] = [];
    const dataType = field.dataType?.toLowerCase();

    if (field.isLookupField) {
      return validators;
    }

    if (dataType !== 'date') {
      const maxLength = parseInt(field.format, 10);
      if (!isNaN(maxLength) && maxLength > 0) {
        validators.push(Validators.maxLength(maxLength));
      }
    }

    switch (dataType) {
      case 'decimal':
        validators.push(customFieldDecimal(field.formatStr, 'Invalid amount'));
        break;

      case 'integer':
        validators.push(integer());
        break;
    }

    return validators;
  }

  getChangedFields(): SaveCustomField[] {
    const changedFields: SaveCustomField[] = [];
    for (const [key, control] of Object.entries(this.fg.controls)) {
      if (control.dirty) {
        const fieldNumber = parseInt(key.split('-')[1], 10);
        const field = this.customFields.find(
          (f) => f.fieldNumber === fieldNumber,
        );
        if (field) {
          let saveValue = control.value;
          let valXRef;

          if (field.valueMethod === 'T' || field.valueMethod === 'L') {
            valXRef = field.valueXref;

            if (control.value && control.value.trim() !== '') {
              saveValue = control.value;
            } else {
              saveValue = null;
            }
          } else if (
            field.dataType === 'date' &&
            control.value instanceof Date
          ) {
            const month = (control.value.getMonth() + 1).toString();
            const day = control.value.getDate().toString();
            const year = control.value.getFullYear();
            saveValue = `${month}/${day}/${year}`;
          }

          changedFields.push({
            miscFieldNumber: field.fieldNumber,
            value: saveValue?.toString(),
            valueXref: valXRef,
            deleted: 'N',
          });
        }
      }
    }
    return changedFields;
  }

  async onExport(format: ExportFormat) {
    const exportData = this.customFields.map((field) => {
      const data: any = {
        tabName: field.tabName,
        fieldNumber: field.fieldNumber,
        longLabel: field.longLabel,
        value: field.value,
      };
      if (!this.isLeave) {
        data.validationDesc = field.validationDesc;
      }

      return data;
    });

    const columns = [
      { name: 'tabName', label: 'Tab Name' },
      { name: 'fieldNumber', label: 'Field #' },
      { name: 'longLabel', label: 'Field' },
      { name: 'value', label: 'Value' },
    ];
    if (!this.isLeave) {
      columns.push({
        name: 'validationDesc',
        label: 'Validation Code Description',
      });
    }

    const formattedDate = this.datePipe.transform(new Date(), 'yyyyMMddHHmm');
    const filename = `CustomFields_${formattedDate}.${format}`;

    if (format === 'csv') {
      const csvData = CustomFieldExportMapper.toCsvExport(
        exportData,
        columns,
        'Custom Fields',
      );
      await this.exportService.exportCsv(csvData, filename);
    } else if (format === 'xlsx') {
      const excelData = CustomFieldExportMapper.toExcelExport(
        exportData,
        columns,
        'Custom Fields',
        'Custom Fields Export',
      );
      await this.exportService.exportExcel(excelData, filename);
    }
  }
}
